/**
 * Product service for handling product-related operations
 */

import axiosClient from "../../config/axiosClient";
import { ENDPOINTS } from "../../config/endpoints";
import { extractApiErrorMessage, showUploadErrorAlert } from "../../helpers/apiError";
import {
  objectToFormData,
  createImageUploadFormData,
} from "../../helpers/formDataHelper";
import {
  Product,
  CreateProductPayload,
  UpdateProductPayload,
  ProductSearchParams,
  ProductResponse,
  ProductsResponse,
  ProductCategoriesResponse,
  ProductSearchResponse,
  BulkUpdatePayload,
  BulkDeletePayload,
  ProductUploadResponse,
  FeaturedProductsResponse,
  TopDealsResponse,
  TopRankingResponse,
  NewArrivalsResponse,
  PopularSuggestionsResponse,
  FrequentlySearchedResponse,
  HeroPromoItemsResponse,
} from "./product.types";

class ProductService {
  /**
   * Get all products with pagination and filters
   */
  async getProducts(params?: ProductSearchParams): Promise<ProductsResponse> {
    const response = await axiosClient.get<ProductsResponse>(
      ENDPOINTS.PRODUCTS.BASE,
      {
        params,
      }
    );
    return response.data;
  }

  /**
   * Get a single product by ID
   */
  async getProductById(id: string): Promise<ProductResponse> {
    const response = await axiosClient.get<ProductResponse>(
      ENDPOINTS.PRODUCTS.byId(id)
    );
    return response.data;
  }

  /**
   * Create a new product
   */
  async createProduct(
    productData: CreateProductPayload
  ): Promise<ProductResponse> {
    const formData = objectToFormData(productData);

    const response = await axiosClient.post<ProductResponse>(
      ENDPOINTS.PRODUCTS.BASE,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  }

  /**
   * Update an existing product
   */
  async updateProduct(
    id: string,
    productData: UpdateProductPayload
  ): Promise<ProductResponse> {
    const formData = objectToFormData(productData);

    const response = await axiosClient.put<ProductResponse>(
      ENDPOINTS.PRODUCTS.byId(id),
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  }

  /**
   * Delete a product
   */
  async deleteProduct(
    id: string
  ): Promise<{ message: string; success: boolean }> {
    const response = await axiosClient.delete<{
      message: string;
      success: boolean;
    }>(ENDPOINTS.PRODUCTS.byId(id));
    return response.data;
  }

  /**
   * Search products with advanced filters
   */
  async searchProducts(
    searchParams: ProductSearchParams
  ): Promise<ProductSearchResponse> {
    const response = await axiosClient.get<ProductSearchResponse>(
      ENDPOINTS.PRODUCTS.SEARCH,
      { params: searchParams }
    );
    return response.data;
  }

  /**
   * Get product categories
   */
  async getCategories(): Promise<ProductCategoriesResponse> {
    const response = await axiosClient.get<ProductCategoriesResponse>(
      ENDPOINTS.PRODUCTS.CATEGORIES
    );
    return response.data;
  }

  /**
   * Get products by category
   */
  async getProductsByCategory(
    categoryId: string,
    params?: Omit<ProductSearchParams, "categoryId">
  ): Promise<ProductsResponse> {
    const response = await axiosClient.get<ProductsResponse>(
      ENDPOINTS.PRODUCTS.byCategory(categoryId),
      { params }
    );
    return response.data;
  }

  /**
   * Get featured products
   */
  async getFeaturedProducts(limit?: number): Promise<FeaturedProductsResponse> {
    const response = await axiosClient.get<any>(
      ENDPOINTS.PRODUCTS.FEATURED,
      { params: { limit } }
    );
    // Handle nested data structure: response.data.data.products
    // or flat structure: response.data.products
    const responseData = response.data;
    if (responseData.data && responseData.data.products) {
      return responseData.data;
    }
    return responseData;
  }

  /**
   * Upload product image
   */
  async uploadProductImage(
    productId: string,
    image: File,
    options?: {
      alt?: string;
      caption?: string;
      isPrimary?: boolean;
    }
  ): Promise<ProductUploadResponse> {
    try {
      const formData = createImageUploadFormData(image, {
        alt: options?.alt,
        caption: options?.caption,
        category: "product",
        tags: ["product", productId],
      });

      const response = await axiosClient.post<ProductUploadResponse>(
        ENDPOINTS.PRODUCTS.UPLOAD_IMAGE,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          params: { productId },
          timeout: 60000,
        }
      );
      return response.data;
    } catch (error) {
      const reason = extractApiErrorMessage(
        error,
        "Product image upload failed. Please try again."
      );
      showUploadErrorAlert(reason);
      throw new Error(reason);
    }
  }

  /**
   * Bulk update products
   */
  async bulkUpdateProducts(
    bulkData: BulkUpdatePayload
  ): Promise<{ message: string; success: boolean; updatedCount: number }> {
    const response = await axiosClient.put<{
      message: string;
      success: boolean;
      updatedCount: number;
    }>(ENDPOINTS.PRODUCTS.BULK_UPDATE, bulkData);
    return response.data;
  }

  /**
   * Bulk delete products
   */
  async bulkDeleteProducts(
    bulkData: BulkDeletePayload
  ): Promise<{ message: string; success: boolean; deletedCount: number }> {
    const response = await axiosClient.delete<{
      message: string;
      success: boolean;
      deletedCount: number;
    }>(ENDPOINTS.PRODUCTS.BULK_DELETE, { data: bulkData });
    return response.data;
  }

  /**
   * Update product inventory
   */
  async updateInventory(
    productId: string,
    inventoryData: {
      quantity?: number;
      reserved?: number;
      lowStockThreshold?: number;
      trackInventory?: boolean;
    }
  ): Promise<ProductResponse> {
    const response = await axiosClient.patch<ProductResponse>(
      `${ENDPOINTS.PRODUCTS.byId(productId)}/inventory`,
      inventoryData
    );
    return response.data;
  }

  /**
   * Update product status
   */
  async updateStatus(
    productId: string,
    status: "draft" | "active" | "inactive" | "discontinued"
  ): Promise<ProductResponse> {
    const response = await axiosClient.patch<ProductResponse>(
      `${ENDPOINTS.PRODUCTS.byId(productId)}/status`,
      { status }
    );
    return response.data;
  }

  /**
   * Toggle product featured status
   */
  async toggleFeatured(productId: string): Promise<ProductResponse> {
    const response = await axiosClient.patch<ProductResponse>(
      `${ENDPOINTS.PRODUCTS.byId(productId)}/featured`
    );
    return response.data;
  }

  /**
   * Get product analytics
   */
  async getProductAnalytics(productId: string): Promise<{
    views: number;
    orders: number;
    revenue: number;
    conversionRate: number;
    message: string;
    success: boolean;
  }> {
    const response = await axiosClient.get<{
      views: number;
      orders: number;
      revenue: number;
      conversionRate: number;
      message: string;
      success: boolean;
    }>(`${ENDPOINTS.PRODUCTS.byId(productId)}/analytics`);
    return response.data;
  }

  /**
   * Get low stock products
   */
  async getLowStockProducts(): Promise<ProductsResponse> {
    const response = await axiosClient.get<ProductsResponse>(
      `${ENDPOINTS.PRODUCTS.BASE}/low-stock`
    );
    return response.data;
  }

  /**
   * Get product recommendations
   */
  async getProductRecommendations(
    productId: string,
    limit?: number
  ): Promise<ProductsResponse> {
    const response = await axiosClient.get<ProductsResponse>(
      `${ENDPOINTS.PRODUCTS.byId(productId)}/recommendations`,
      { params: { limit } }
    );
    return response.data;
  }

  /**
   * Get top deals
   */
  async getTopDeals(limit?: number): Promise<TopDealsResponse> {
    const response = await axiosClient.get<any>(
      ENDPOINTS.PRODUCTS.TOP_DEALS,
      { params: { limit } }
    );
    // Handle nested data structure: response.data.data.products
    // or flat structure: response.data.products
    const responseData = response.data;
    if (responseData.data && responseData.data.products) {
      return responseData.data;
    }
    return responseData;
  }

  /**
   * Get top ranking products
   */
  async getTopRanking(limit?: number): Promise<TopRankingResponse> {
    const response = await axiosClient.get<any>(
      ENDPOINTS.PRODUCTS.TOP_RANKING,
      { params: { limit } }
    );
    // Handle nested data structure: response.data.data.products
    // or flat structure: response.data.products
    const responseData = response.data;
    if (responseData.data && responseData.data.products) {
      return responseData.data;
    }
    return responseData;
  }

  /**
   * Get new arrivals
   */
  async getNewArrivals(limit?: number): Promise<NewArrivalsResponse> {
    const response = await axiosClient.get<any>(
      ENDPOINTS.PRODUCTS.NEW_ARRIVALS,
      { params: { limit } }
    );
    // Handle nested data structure: response.data.data.products
    // or flat structure: response.data.products
    const responseData = response.data;
    if (responseData.data && responseData.data.products) {
      return responseData.data;
    }
    return responseData;
  }

  /**
   * Get popular search suggestions
   */
  async getPopularSuggestions(
    query?: string,
    limit?: number
  ): Promise<PopularSuggestionsResponse> {
    const response = await axiosClient.get<any>(
      ENDPOINTS.PRODUCTS.POPULAR_SUGGESTIONS,
      { params: { query, limit } }
    );
    // Handle nested data structure: response.data.data.suggestions
    // or flat structure: response.data.suggestions
    const responseData = response.data;
    if (responseData.data && responseData.data.suggestions) {
      return responseData.data;
    }
    return responseData;
  }

  /**
   * Get frequently searched products/terms for hero (category name, optional image, slug).
   * Returns empty array on error or when none available.
   */
  async getFrequentlySearched(limit?: number): Promise<FrequentlySearchedResponse> {
    const response = await axiosClient.get<any>(
      ENDPOINTS.PRODUCTS.FREQUENTLY_SEARCHED,
      { params: { limit } }
    );
    const responseData = response.data;
    if (responseData?.data?.items) return responseData.data;
    if (responseData?.items) return responseData;
    return { items: [] };
  }

  /**
   * Get hero promo items (title, ctaText, ctaUrl) for hero section.
   * Returns empty array on error or when none available.
   */
  async getHeroPromoItems(limit?: number): Promise<HeroPromoItemsResponse> {
    const response = await axiosClient.get<any>(
      ENDPOINTS.PRODUCTS.HERO_PROMO_ITEMS,
      { params: { limit } }
    );
    const responseData = response.data;
    if (responseData?.data?.items) return responseData.data;
    if (responseData?.items) return responseData;
    return { items: [] };
  }
}

// Export singleton instance
export const productService = new ProductService();
export default productService;
